/**
 * TanStack Router configuration for Black Wall.
 */

import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { createContext, useContext } from "react";
import { AppShell } from "./layouts/AppShell";
import { GuestShell } from "./layouts/GuestShell";

export type Role = "admin" | "guest";

/** React context — carries the authenticated role into route components. */
export const RoleContext = createContext<Role>("admin");

// Route components
import { LoginRoute } from "./routes/login";
import { ChatRoute } from "./routes/chat";
import { CrewRoute } from "./routes/crew";
import { McpRoute } from "./routes/mcp";
import { SubagentsRoute } from "./routes/subagents";
import { ProjectsRoute } from "./routes/projects";
import { ProjectDetailRoute } from "./routes/project-detail";
import { ContextRoute } from "./routes/context";
import { SkillsRoute } from "./routes/skills";
import { OpsRoute } from "./routes/ops";
import { TerminalRoute } from "./routes/terminal";
import { SecurityRoute } from "./routes/security";
import { LogsRoute } from "./routes/logs";
import { NotificationsRoute } from "./routes/notifications";
import { GymRoute } from "./routes/gym";
import { GymTodayRoute } from "./routes/gym-today";
import { GamingRoute } from "./routes/gaming";
import { CardsRoute } from "./routes/cards";
import { CostsRoute } from "./routes/costs";
import { EditorRoute } from "./routes/editor";
import { HabitsRoute } from "./routes/habits";
import { LearningRoute } from "./routes/learning";
import { CentinelasRoute } from "./routes/centinelas";
import { BriefRoute } from "./routes/brief";
import { SettingsRoute } from "./routes/settings";

/** Check session with the API — returns role alongside auth status. */
async function checkAuth(): Promise<{ authenticated: boolean; role: Role }> {
  try {
    const res = await fetch("/api/auth/session", { credentials: "include" });
    if (!res.ok) return { authenticated: false, role: "admin" };
    const data = await res.json();
    return {
      authenticated: data.authenticated === true,
      role: (data.role as Role) ?? "admin",
    };
  } catch {
    return { authenticated: false, role: "admin" };
  }
}

// Module-level role, set by beforeLoad so the component can read it.
// Safe for single-user: only one session exists at a time.
let _role: Role = "admin";

// Root route with auth check + shell selection
const rootRoute = createRootRoute({
  beforeLoad: async ({ location }) => {
    // Skip auth check for login page
    if (location.pathname === "/login") return;
    const { authenticated, role } = await checkAuth();
    if (!authenticated) {
      throw redirect({ to: "/login" });
    }
    _role = role;
    // Guests can only access /chat
    if (role === "guest" && location.pathname !== "/chat") {
      throw redirect({ to: "/chat", search: { agent: undefined, session: undefined } });
    }
  },
  component: () => {
    const path = window.location.pathname;
    if (path === "/login") return <Outlet />;
    // The Terminal Surface renders full-viewport, outside the AppShell chrome.
    if (path === "/terminal") return <Outlet />;
    if (_role === "guest") {
      return (
        <RoleContext.Provider value="guest">
          <GuestShell>
            <Outlet />
          </GuestShell>
        </RoleContext.Provider>
      );
    }
    return (
      <RoleContext.Provider value="admin">
        <AppShell>
          <Outlet />
        </AppShell>
      </RoleContext.Provider>
    );
  },
});

// Login route — no AppShell, no auth required
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: async () => {
    const { authenticated } = await checkAuth();
    if (authenticated) {
      throw redirect({ to: "/chat", search: { agent: undefined, session: undefined } });
    }
  },
  component: LoginRoute,
});

// Index route - redirect to /chat
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/chat", search: { agent: undefined, session: undefined } });
  },
});

// Chat route (with optional search params)
const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat",
  component: ChatRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    agent: search.agent as string | undefined,
    session: search.session as string | undefined,
  }),
});

// Crew route
const crewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crew",
  component: CrewRoute,
});

// MCP route
const mcpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mcp",
  component: McpRoute,
});

// Subagents route
const subagentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/subagents",
  component: SubagentsRoute,
});

// Projects route
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  component: ProjectsRoute,
});

// Project detail route
const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$name",
  component: ProjectDetailRoute,
});

// Context route
const contextRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/context",
  component: ContextRoute,
});

// Skills route
const skillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/skills",
  component: SkillsRoute,
});

// Ops route
const opsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops",
  component: OpsRoute,
});

// Terminal Surface — full-viewport, chrome-free (see root component).
const terminalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terminal",
  component: TerminalRoute,
});

// Security route
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: SecurityRoute,
});

// Logs route
const logsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/logs",
  component: LogsRoute,
});

// Notifications route
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: NotificationsRoute,
});

// Gym route
const gymRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gym",
  component: GymRoute,
});

const gymTodayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gym/today",
  component: GymTodayRoute,
});

const gamingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gaming",
  component: GamingRoute,
});

const cardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cards",
  component: CardsRoute,
});

// Costs route
const costsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/costs",
  component: CostsRoute,
});

// Editor route
const editorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/editor",
  component: EditorRoute,
});

// Habits route
const habitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/habits",
  component: HabitsRoute,
});

// Learning route
const learningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/learning",
  component: LearningRoute,
});

// Centinelas route
const centinelasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/centinelas",
  component: CentinelasRoute,
});

// Daily Brief route
const briefRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brief",
  component: BriefRoute,
});

// Settings route
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsRoute,
});

// Build route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  chatRoute,
  crewRoute,
  mcpRoute,
  subagentsRoute,
  projectsRoute,
  projectDetailRoute,
  contextRoute,
  skillsRoute,
  opsRoute,
  terminalRoute,
  securityRoute,
  logsRoute,
  notificationsRoute,
  gymRoute,
  gymTodayRoute,
  gamingRoute,
  cardsRoute,
  costsRoute,
  editorRoute,
  habitsRoute,
  learningRoute,
  centinelasRoute,
  briefRoute,
  settingsRoute,
]);

// Create router
export const router = createRouter({ routeTree });

// Type declaration for router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
